import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Stack,
  NativeSelect,
  Spinner,
  Text,
  SimpleGrid,
  Card,
  Avatar,
  Badge,
  IconButton,
  Input,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { organizationsAPI, projectsAPI } from '../services/api';
import { Organization, Project } from '../types';
import { toast } from '../utils/toast';

interface OrgWithRole extends Organization {
  role?: 'owner' | 'admin' | 'member';
}

const Dashboard: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrgWithRole[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const isAdmin = user?.role === 'admin';

  const renderNavButtons = () => {
    // Trenutno se koristi samo za horizontalni layout u headeru (ako je vidljiv),
    // pa zadržavamo jednostavnu verziju sa svim stavkama u nizu.
    return (
      <>
        {(isMasterAdmin || isAdmin) && (
          <Button
            variant="outline"
            onClick={() => navigate('/admin')}
            borderRadius="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'md',
            }}
          >
            Admin Panel
          </Button>
        )}
        <HStack gap={2}>
          <Avatar.Root size="sm">
            <Avatar.Fallback bg="gray.200" color="black">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar.Fallback>
          </Avatar.Root>
          <Text fontWeight="medium" color="gray.700">
            {user?.name}
          </Text>
        </HStack>
        <Button
          variant="outline"
          onClick={() => navigate('/settings')}
          borderRadius="lg"
          _hover={{ bg: 'gray.100' }}
        >
          Settings
        </Button>
        <Button
          variant="outline"
          onClick={logout}
          borderRadius="lg"
          _hover={{
            bg: 'gray.100',
          }}
        >
          Logout
        </Button>
      </>
    );
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadProjects = useCallback(async (orgId: string) => {
    try {
      const response = await projectsAPI.getByOrganization(orgId, showArchived);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load projects';
      toast({
        title: 'Error',
        description: msg,
        status: 'error',
      });
      setProjects([]);
    }
  }, [showArchived]);

  useEffect(() => {
    if (selectedOrg) {
      loadProjects(selectedOrg);
    }
  }, [selectedOrg, loadProjects]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      const data = Array.isArray(response.data) ? response.data : [];
      setOrganizations(data);
      if (data.length > 0) {
        setSelectedOrg(data[0]._id);
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to load organizations';
      toast({
        title: 'Error',
        description: msg,
        status: 'error',
      });
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };


  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const filteredProjects = projects.filter((project) => {
    if (!searchTerm.trim()) {
      return true;
    }
    const term = searchTerm.toLowerCase();
    const name = (project.name || '').toLowerCase();
    return name.includes(term);
  });

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top={0}
        left={0}
        h="100vh"
        w="260px"
        bg="white"
        borderRightWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        p={4}
        flexDirection="column"
        zIndex={15}
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.06}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src="/NS_Projects.png"
            alt="NS Projects watermark"
            style={{ maxWidth: '260px' }}
          />
        </Box>

        <Box
          position="relative"
          zIndex={1}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Heading
            size="lg"
            mb={2}
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Project Manager
          </Heading>
          <HStack gap={2} mb={6}>
            <Avatar.Root size="sm">
              <Avatar.Fallback bg="gray.200" color="black">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar.Fallback>
            </Avatar.Root>
            <Text fontWeight="medium" color="gray.700">
              {user?.name}
            </Text>
          </HStack>
          <VStack align="stretch" gap={3} flex="1">
            {(isMasterAdmin || isAdmin) && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                justifyContent="flex-start"
                width="100%"
              >
                Admin Panel
              </Button>
            )}
          </VStack>
          <VStack
            align="stretch"
            gap={2}
            pt={3}
            borderTopWidth={1}
            borderColor="gray.100"
          >
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              borderRadius="lg"
              _hover={{ bg: 'gray.100' }}
              justifyContent="flex-start"
              width="100%"
            >
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              borderRadius="lg"
              _hover={{
                bg: 'gray.100',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Logout
            </Button>
          </VStack>
        </Box>
      </Box>

      {/* Mobile header */}
      <Box
        bg="white"
        borderBottomWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        display={{ base: 'block', md: 'none' }}
      >
        <Container maxW="container.xl" py={4}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={{ base: 3, md: 4 }}
          >
            <HStack w="100%" justifyContent="space-between" alignItems="center">
              <Heading
                size="xl"
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                Project Manager
              </Heading>
              <IconButton
                aria-label="Open navigation"
                variant="ghost"
                borderRadius="full"
                display={{ base: 'inline-flex', md: 'none' }}
                onClick={() => setMobileNavOpen(true)}
              >
                <svg
                  width="22"
                  height="22"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </IconButton>
            </HStack>
            {/* Horizontal nav (trenutno skriven na većim ekranima zajedno sa headerom) */}
          </Stack>
        </Container>
      </Box>

      {/* Mobile sidebar navigation */}
      {mobileNavOpen && (
        <Box display={{ base: 'block', md: 'none' }}>
          <Box
            position="fixed"
            inset={0}
            bg="blackAlpha.400"
            zIndex={20}
            onClick={() => setMobileNavOpen(false)}
          />
          <Box
            position="fixed"
            top={0}
            left={0}
            h="100vh"
            w="260px"
            bg="white"
            boxShadow="xl"
            zIndex={21}
            p={4}
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset={0}
              opacity={0.06}
              pointerEvents="none"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src="/NS_Projects.png"
                alt="NS Projects watermark"
                style={{ maxWidth: '260px' }}
              />
            </Box>

            <VStack align="stretch" gap={4} h="100%" position="relative" zIndex={1}>
              <HStack justifyContent="space-between" mb={1}>
                <Heading size="md" color="gray.800">
                  Menu
                </Heading>
                <IconButton
                  aria-label="Close navigation"
                  variant="ghost"
                  borderRadius="full"
                  onClick={() => setMobileNavOpen(false)}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </IconButton>
              </HStack>
              <HStack gap={2} mb={2}>
                <Avatar.Root size="sm">
                  <Avatar.Fallback bg="gray.200" color="black">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar.Fallback>
                </Avatar.Root>
                <Text fontWeight="medium" color="gray.700">
                  {user?.name}
                </Text>
              </HStack>
              <VStack align="stretch" gap={3} flex="1">
                {(isMasterAdmin || isAdmin) && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin')}
                    borderRadius="lg"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                    }}
                    justifyContent="flex-start"
                    width="100%"
                  >
                    Admin Panel
                  </Button>
                )}
              </VStack>
              <VStack
                align="stretch"
                gap={2}
                pt={2}
                borderTopWidth={1}
                borderColor="gray.100"
              >
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings')}
                  borderRadius="lg"
                  _hover={{ bg: 'gray.100' }}
                  justifyContent="flex-start"
                  width="100%"
                >
                  Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                  borderRadius="lg"
                  _hover={{
                    bg: 'gray.100',
                  }}
                  justifyContent="flex-start"
                  width="100%"
                >
                  Logout
                </Button>
              </VStack>
            </VStack>
          </Box>
        </Box>
      )}

      <Container
        maxW={{ base: 'container.xl', md: '100%' }}
        py={8}
        pl={{ base: 4, md: '280px' }}
        pr={{ base: 4, md: 8 }}
      >
        <VStack gap={6} align="stretch">
          {/* Controls */}
          <Stack
            direction={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'stretch', md: 'center' }}
            justifyContent="space-between"
            gap={4}
            flexWrap="wrap"
          >
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              flex="1"
              minW={0}
            >
              <NativeSelect.Root
                maxW={{ base: '100%', md: '260px' }}
                width={{ base: '100%', md: 'auto' }}
                flexShrink={0}
              >
                <NativeSelect.Field
                  value={selectedOrg}
                  onChange={(e: any) => setSelectedOrg(e.target.value)}
                  borderRadius="lg"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                >
                  {organizations.map((org) => (
                    <option key={org._id} value={org._id}>
                      {org.name}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="lg"
                maxW={{ base: '100%', md: '400px' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
              />
            </Stack>
            <HStack gap={3} flexShrink={0}>
              <Button
                variant={showArchived ? 'solid' : 'outline'}
                onClick={() => setShowArchived(!showArchived)}
                borderRadius="lg"
                colorScheme="gray"
                flex={{ base: 1, md: 'none' }}
              >
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={() => navigate('/project/new')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                fontWeight="semibold"
                flex={{ base: 1, md: 'none' }}
              >
                <HStack gap={2} justifyContent="center">
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>New Project</span>
                </HStack>
              </Button>
            </HStack>
          </Stack>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <Box 
              textAlign="center" 
              py={20}
              bg="white"
              borderRadius="xl"
              borderWidth={2}
              borderStyle="dashed"
              borderColor="gray.300"
            >
              <Box mb={4}>
                <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto', color: '#9CA3AF' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </Box>
              <Text fontSize="xl" fontWeight="semibold" color="gray.600" mb={2}>
                No projects yet
              </Text>
              <Text fontSize="md" color="gray.500" mb={6}>
                Create your first project to get started!
              </Text>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={() => navigate('/project/new')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
              >
                Create Project
              </Button>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
              {filteredProjects.map((project) => (
                <Card.Root
                  key={project._id}
                  cursor="pointer"
                  onClick={() => handleProjectClick(project._id)}
                  bg="white"
                  borderRadius="xl"
                  borderWidth={1}
                  borderColor="gray.200"
                  overflow="hidden"
                  transition="all 0.3s"
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: 'xl',
                    borderColor: 'blue.300',
                  }}
                >
                  <Box
                    h="4px"
                    bgGradient="to-r"
                    gradientFrom="#273f96"
                    gradientTo="#1369b3"
                  />
                  <Card.Body p={6}>
                    <HStack justifyContent="space-between" mb={3}>
                      <Heading size="md" color="gray.800">
                        {project.name}
                      </Heading>
                      <Badge 
                        colorScheme={project.status === 'archived' ? 'gray' : 'green'} 
                        borderRadius="full" 
                        px={2}
                      >
                        {project.status === 'archived' ? 'Archived' : 'Active'}
                      </Badge>
                    </HStack>
                    <Text 
                      color="gray.600" 
                      fontSize="sm"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.5'
                      } as React.CSSProperties}
                    >
                      {project.description || 'No description provided'}
                    </Text>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default Dashboard;
